import { Box, Typography } from '@mui/material'
import React from 'react'

import ImgTag from '../ImgTag'
import { Tag } from '../Tag'

interface IProductItemDetailTagProps {
  item: IItemProductItemDetailTag
}

export interface IItemProductItemDetailTag {
  url?: string
  name: string
  quantity?: number
  isCollection?: boolean
  commissionName?: string
  color?: string
  size?: string
}

// keyword -> swatch. Matching is substring-based because real data looks like
// 'น้ำเงินเข้ม' / 'Navy Blue' rather than a bare color name. The list is sorted by
// keyword length at load time so a longer keyword always wins over a shorter one it
// contains ('น้ำเงิน' before 'เงิน', 'ทองแดง' before 'ทอง'), and ties keep the order
// written here (Array.prototype.sort is stable), so 'navy' stays ahead of 'blue'.
const normalizeColorValue = (value: string) =>
  (value ?? '').toLowerCase().replace(/[\s_./-]/g, '')

const COLOR_SWATCHES: { keyword: string; hex: string }[] = [
  // red / pink
  { keyword: 'แดงเลือดนก', hex: '#a4161a' },
  { keyword: 'เลือดหมู', hex: '#6e1423' },
  { keyword: 'เบอร์กันดี', hex: '#6b1f35' },
  { keyword: 'ไวน์', hex: '#6e2639' },
  { keyword: 'ทับทิม', hex: '#b3123d' },
  { keyword: 'บานเย็น', hex: '#d81b60' },
  { keyword: 'โอลด์โรส', hex: '#c48b8b' },
  { keyword: 'โรสโกลด์', hex: '#e0a899' },
  { keyword: 'แซลมอน', hex: '#fa8072' },
  { keyword: 'คอรัล', hex: '#ff6f61' },
  { keyword: 'พีช', hex: '#ffb997' },
  { keyword: 'นู้ด', hex: '#e3bfae' },
  { keyword: 'ชมพู', hex: '#f472b6' },
  { keyword: 'แดง', hex: '#e53935' },
  // orange / yellow / brown
  { keyword: 'ส้มอิฐ', hex: '#b7410e' },
  { keyword: 'อิฐ', hex: '#9c4a2f' },
  { keyword: 'แสด', hex: '#f4511e' },
  { keyword: 'แอปริคอต', hex: '#f4a261' },
  { keyword: 'มัสตาร์ด', hex: '#d4a017' },
  { keyword: 'งาช้าง', hex: '#f6f1e3' },
  { keyword: 'ไอวอรี่', hex: '#f6f1e3' },
  { keyword: 'แชมเปญ', hex: '#efe1c6' },
  { keyword: 'ครีม', hex: '#f5e6c8' },
  { keyword: 'เบจ', hex: '#e3d5b8' },
  { keyword: 'กากี', hex: '#bdb385' },
  { keyword: 'ช็อกโกแลต', hex: '#5b3a29' },
  { keyword: 'โกโก้', hex: '#6b4423' },
  { keyword: 'กาแฟ', hex: '#6f4e37' },
  { keyword: 'น้ำตาล', hex: '#8b5e34' },
  { keyword: 'ทองแดง', hex: '#b87333' },
  { keyword: 'เหลือง', hex: '#f5c518' },
  { keyword: 'ส้ม', hex: '#f97316' },
  { keyword: 'ทอง', hex: '#d4a017' },
  // green
  { keyword: 'เขียวขี้ม้า', hex: '#6b705c' },
  { keyword: 'เขียวมะกอก', hex: '#808000' },
  { keyword: 'เขียวหัวเป็ด', hex: '#0f766e' },
  { keyword: 'เขียวตอง', hex: '#4caf50' },
  { keyword: 'เขียวมินต์', hex: '#a8e6cf' },
  { keyword: 'มินต์', hex: '#a8e6cf' },
  { keyword: 'มิ้นท์', hex: '#a8e6cf' },
  { keyword: 'มรกต', hex: '#10b981' },
  { keyword: 'โอลีฟ', hex: '#808000' },
  { keyword: 'มะนาว', hex: '#c7ea46' },
  { keyword: 'เขียว', hex: '#22a06b' },
  // blue
  { keyword: 'กรมท่า', hex: '#1f2a55' },
  { keyword: 'เนวี่', hex: '#1f2a55' },
  { keyword: 'ไพลิน', hex: '#1e40af' },
  { keyword: 'เทอร์ควอยซ์', hex: '#40e0d0' },
  { keyword: 'ฟ้าคราม', hex: '#4a6fa5' },
  { keyword: 'น้ำเงิน', hex: '#1e3a8a' },
  { keyword: 'คราม', hex: '#3f51b5' },
  { keyword: 'ฟ้า', hex: '#38bdf8' },
  // purple
  { keyword: 'ลาเวนเดอร์', hex: '#b39ddb' },
  { keyword: 'ไลแลค', hex: '#c8a2c8' },
  { keyword: 'ม่วง', hex: '#8b5cf6' },
  // neutral
  { keyword: 'เทาควันบุหรี่', hex: '#8a8a8a' },
  { keyword: 'ชาโคล', hex: '#36454f' },
  { keyword: 'เทา', hex: '#9ca3af' },
  { keyword: 'เงิน', hex: '#c0c4cc' },
  { keyword: 'ขาว', hex: '#ffffff' },
  { keyword: 'ดำ', hex: '#2b2b2b' },
  // english — red / pink
  { keyword: 'burgundy', hex: '#6b1f35' },
  { keyword: 'crimson', hex: '#a4161a' },
  { keyword: 'maroon', hex: '#6e1423' },
  { keyword: 'magenta', hex: '#d81b60' },
  { keyword: 'rosegold', hex: '#e0a899' },
  { keyword: 'salmon', hex: '#fa8072' },
  { keyword: 'coral', hex: '#ff6f61' },
  { keyword: 'peach', hex: '#ffb997' },
  { keyword: 'nude', hex: '#e3bfae' },
  { keyword: 'rose', hex: '#c48b8b' },
  { keyword: 'pink', hex: '#f472b6' },
  { keyword: 'red', hex: '#e53935' },
  // english — orange / yellow / brown
  { keyword: 'apricot', hex: '#f4a261' },
  { keyword: 'mustard', hex: '#d4a017' },
  { keyword: 'champagne', hex: '#efe1c6' },
  { keyword: 'chocolate', hex: '#5b3a29' },
  { keyword: 'orange', hex: '#f97316' },
  { keyword: 'coffee', hex: '#6f4e37' },
  { keyword: 'bronze', hex: '#b87333' },
  { keyword: 'copper', hex: '#b87333' },
  { keyword: 'yellow', hex: '#f5c518' },
  { keyword: 'ivory', hex: '#f6f1e3' },
  { keyword: 'cream', hex: '#f5e6c8' },
  { keyword: 'beige', hex: '#e3d5b8' },
  { keyword: 'khaki', hex: '#bdb385' },
  { keyword: 'amber', hex: '#ffbf00' },
  { keyword: 'brown', hex: '#8b5e34' },
  { keyword: 'gold', hex: '#d4a017' },
  // english — green / blue / purple
  { keyword: 'turquoise', hex: '#40e0d0' },
  { keyword: 'emerald', hex: '#10b981' },
  { keyword: 'lavender', hex: '#b39ddb' },
  { keyword: 'indigo', hex: '#3f51b5' },
  { keyword: 'violet', hex: '#7c3aed' },
  { keyword: 'purple', hex: '#8b5cf6' },
  { keyword: 'olive', hex: '#808000' },
  { keyword: 'green', hex: '#22a06b' },
  { keyword: 'lilac', hex: '#c8a2c8' },
  { keyword: 'navy', hex: '#1f2a55' },
  { keyword: 'teal', hex: '#0f766e' },
  { keyword: 'blue', hex: '#2563eb' },
  { keyword: 'mint', hex: '#a8e6cf' },
  { keyword: 'lime', hex: '#c7ea46' },
  { keyword: 'aqua', hex: '#4dd0e1' },
  { keyword: 'cyan', hex: '#22d3ee' },
  // english — neutral
  { keyword: 'charcoal', hex: '#36454f' },
  { keyword: 'silver', hex: '#c0c4cc' },
  { keyword: 'black', hex: '#2b2b2b' },
  { keyword: 'white', hex: '#ffffff' },
  { keyword: 'grey', hex: '#9ca3af' },
  { keyword: 'gray', hex: '#9ca3af' },
]
  .map((swatch) => ({
    ...swatch,
    keyword: normalizeColorValue(swatch.keyword),
  }))
  .sort((a, b) => b.keyword.length - a.keyword.length)

const resolveColorSwatch = (color: string) => {
  const raw = (color ?? '').trim().toLowerCase()
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(raw)) return raw
  const value = normalizeColorValue(raw)
  if (!value) return undefined
  return COLOR_SWATCHES.find((swatch) => value.includes(swatch.keyword))?.hex
}

const chipBaseSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  height: 18,
  borderRadius: 99,
  paddingX: 0.75,
  maxWidth: '100%',
}

const chipTextSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const ColorChip = ({ color }: { color: string }) => {
  const swatch = resolveColorSwatch(color)
  return (
    <Box
      sx={{
        ...chipBaseSx,
        backgroundColor: '#ffffff',
        border: '1px solid',
        borderColor: 'rgba(15, 23, 42, 0.12)',
      }}
    >
      {!!swatch && (
        <Box
          sx={{
            width: 9,
            height: 9,
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: swatch,
            boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.18)',
          }}
        />
      )}
      <Typography
        component="span"
        fontSize={10.5}
        fontWeight={600}
        lineHeight={1}
        color="text.primary"
        sx={chipTextSx}
      >
        {color}
      </Typography>
    </Box>
  )
}

const SizeChip = ({ size }: { size: string }) => (
  <Box
    sx={{
      ...chipBaseSx,
      gap: 0.375,
      backgroundColor: 'rgba(2, 136, 209, 0.12)',
    }}
  >
    <Typography
      component="span"
      fontSize={9.5}
      lineHeight={1}
      color="info.main"
      sx={{ flexShrink: 0, opacity: 0.8 }}
    >
      ไซส์
    </Typography>
    <Typography
      component="span"
      fontSize={10.5}
      fontWeight={700}
      lineHeight={1}
      color="info.dark"
      sx={chipTextSx}
    >
      {size}
    </Typography>
  </Box>
)

const ProductItemDetailTag = (props: IProductItemDetailTagProps) => {
  const { item } = props
  const { isCollection, color, size, commissionName, quantity } = item
  const hasMeta = !!color || !!size

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        mb: 0.75,
        padding: '6px 8px 6px 7px',
        backgroundColor: '#ffffff',
        border: '1px solid',
        borderColor: 'rgba(2, 136, 209, 0.2)',
        borderLeft: '3px solid',
        borderLeftColor: 'info.main',
        borderRadius: 1.5,
        textAlign: 'left',
        transition: 'background-color 0.2s, box-shadow 0.2s',
        '&:hover': {
          backgroundColor: '#f4fbfd',
          boxShadow: '0 2px 6px rgba(2, 136, 209, 0.16)',
        },
      }}
    >
      <Box sx={{ flexShrink: 0, pt: '1px' }}>
        <ImgTag imageUrl={item.url} name={item.name} size={30} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="subtitle2"
          fontSize={12}
          lineHeight={1.4}
          color="info.main"
          textAlign={'left'}
        >
          {item.name}
          {!!quantity && (
            <Typography
              component="span"
              fontSize={11}
              fontWeight={700}
              color="text.secondary"
            >
              {' '}
              x {quantity}
            </Typography>
          )}{' '}
          {isCollection && <>(เซ็ต)</>}
          {!!commissionName && (
            <Tag color="warning" style={{ fontSize: 10 }}>
              {commissionName}
            </Tag>
          )}
        </Typography>
        {hasMeta && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.625,
            }}
          >
            {!!color && <ColorChip color={color} />}
            {!!size && <SizeChip size={size} />}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ProductItemDetailTag
